import React, {useEffect, useState} from 'react';
import { useQuery } from '/src/api';
import { useModalsContext, useStoreConfigContext } from '/src/providers';
import { Modal, Form, useForm, H2 } from '/src/components';
import { COOKIE_CONSENT } from '/src/consts';
import { getCookie, setCookie } from '/src/utils';
import {googleCookiesConsentUpdate, googleLoadTagManager} from '/src/services';
import graphQLQueries from './graph-ql-queries';
import * as styles from "./styles.scss";

const CookieItem = ({
    name,
    value,
    label,
    handleChange,
    disabled,
    children,
}) => {
    const [isOpened, setIsOpened] = useState(false);

    return (
        <div
            className={styles.item}
            onClick={(e) => {
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'LABEL') {
                    e.stopPropagation();
                    return;
                }

                setIsOpened(!isOpened);
            }}
        >
            <div className={`${styles.titleWrapper}${isOpened ? ` ${styles.opened}` : '' }`}>
                <Form.Checkbox
                    label={label}
                    name={name}
                    checked={value}
                    onChange={handleChange}
                    disabled={disabled}
                />
            </div>
            {
                isOpened &&
                <div className={styles.content}>
                    {children}
                </div>
            }
        </div>
    )
};

const CookieConsent = () => {
    const {
        storeConfig: {
            googletagmanager_container_id,
        },
        translate
    } = useStoreConfigContext();
    const { hideModal } = useModalsContext();
    const {
        data: cookieConsent,
        isLoading,
        runQuery,
    } = useQuery({
        query: graphQLQueries.cookieConsent,
        queryKey: ['getCookieConsentGroups']
    });
    const { data, handleChange, setFormValues } = useForm(null);
    const [isSettings, setIsSetting] = useState(false);

    const handleEnd = data => {
        setCookie(COOKIE_CONSENT, JSON.stringify(data), 365);
        googleLoadTagManager(googletagmanager_container_id || 'GTM-TX3RJRV');
        googleCookiesConsentUpdate();
        hideModal();
    };

    const handleAccept = () => handleEnd(data);

    const handleAcceptAll = () => {
        const accepted = {};

        Object.keys(data).forEach(key => {
            accepted[key] = data[key] === 2 ? 2 : 1;
        });

        handleEnd(accepted);
    };

    const onChange = e => {
        const { name, checked, type } = e.target;
        handleChange({ target: { name, checked: checked ? 1 : 0, type } });
    };

    useEffect(() => {
        if (cookieConsent) {
            const settings = JSON.parse(getCookie(COOKIE_CONSENT) || '{}');

            cookieConsent.forEach(({ group_id, default_status }) => {
                if (settings[group_id] === undefined) {
                    settings[group_id] = default_status;
                }
            });

            setFormValues({ data: settings });
        }
    }, [cookieConsent]);

    useEffect(() => {
        runQuery();
    }, []);

    return (
        !!data &&
        <Modal>
            {
                !isSettings &&
                <div className={styles.modalWrapper}>
                    <H2>{translate('Taste the ultimate buy whole foods online experience')}</H2>
                    <span className={styles.content}>
                        {translate('We want to give you the very best service during your search for the highest quality foods.')}
                    </span>
                    <span className={styles.content}>
                        {translate('By clicking “Accept All Cookies”, you agree to the storing of cookies on your device to enhance site navigation, analyse site usage, and assist in our marketing efforts.')}
                    </span>
                    <span className={styles.content}>
                        {translate('Do not worry, all of our cookies are made from the best quality organic ingredients!')}
                    </span>
                    <div className={`${styles.actionsWrapper} ${styles.actionsWrapperWide}`}>
                        <button
                            className={`${styles.button} ${styles.linkButton}`}
                            onClick={() => setIsSetting(true)}
                            disabled={isLoading}
                        >
                            {translate('Customize')}
                        </button>
                        <button
                            className={`${styles.button} ${styles.accept}`}
                            onClick={handleAcceptAll}
                            disabled={isLoading}
                        >
                            {translate('Accept all')}
                        </button>
                    </div>
                </div>
            }
            {
                isSettings &&
                <div className={`${styles.modalWrapper} ${styles.modalWrapperInner}`}>
                    <H2>{translate('Privacy preference center')}</H2>
                    <span className={styles.content}>
                        {translate('When you visit any website, it may store or retrieve information on your browser, mostly in the form of cookies.')}
                    </span>
                    <span className={styles.content}>
                        {translate('This information might be about you, your preferences or your device and is mostly used to make the site work as you expect it to. The information does not usually directly identify you, but it can give you a more personalized web experience.')}
                    </span>
                    <span className={styles.content}>
                        {translate(' Because we respect your right to privacy, you can choose not to allow some types of cookies.')}
                    </span>
                    <span className={styles.content}>
                        {translate('Click on the different category headings to find out more and change our default settings. However, blocking some types of cookies may impact your experience of the site and the services we are able to offer.')}
                    </span>
                    <H2>{translate('Manage consent preferences')}</H2>
                    {
                        cookieConsent?.map(({ group_id, tab_title, tab_description }) => (
                            <CookieItem
                                key={group_id}
                                name={group_id}
                                value={data[group_id]}
                                label={tab_title}
                                handleChange={onChange}
                                disabled={isLoading || data[group_id] === 2}
                            >
                                {tab_description}
                            </CookieItem>
                        ))
                    }
                    <div className={styles.actionsWrapper}>
                        <button
                            className={`${styles.button} ${styles.linkButton}`}
                            onClick={() => setIsSetting(false)}
                            disabled={isLoading}
                        >
                            {translate('Back')}
                        </button>
                        <button
                            className={`${styles.button} ${styles.accept}`}
                            disabled={isLoading}
                            onClick={handleAccept}
                        >
                            {translate('Accept')}
                        </button>
                    </div>
                </div>

            }
        </Modal>
    );
};

export default CookieConsent;
